import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useFormik } from 'formik';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { updateEOneItemMutation, createAuditMutation, findRequestNumberFromBarcode } from './api';
import Statuses from './components/Statuses';
import * as selectDropdowns from './utils';
import { Comments } from '../Comments';

interface Props {
  data: any;
  queryData: any;
}

const EditEOneItemForm = (props: Props) => {
  const [updateEOne] = useMutation(updateEOneItemMutation);
  const [comment, setComment] = React.useState('');
  const [createAudit] = useMutation(createAuditMutation);
  const [ elevated, setElevated] = React.useState(false)
  const { user } = useSelector((state: any) => ({
    user: state.user,
  }));

   React.useEffect(() => {
     let enableDisabledFields = false;
     if (user.roles) {
       user.roles.forEach((role) => {
         switch (role.roleId) {
           case 1: // admin
           case 3: // pc - non cap
           case 10: // pc - cap
           case 21: // rec super
             enableDisabledFields = true;
         }
       });
     }
     setElevated(enableDisabledFields);
   }, []);

  const initialFormikValues = {
    id: props.queryData.readOneById.id,
    assetNumber: props.queryData.readOneById.assetNumber,
    description1: props.queryData.readOneById.description1,
    description2: props.queryData.readOneById.description2,
    description3: props.queryData.readOneById.description3,
    updateStatus: props.queryData.readOneById.updateStatus,
    accountNumber: props.queryData.readOneById.accountNumber,
    additionalRemarks: props.queryData.readOneById.additionalRemarks,
    additionalRemarks2: props.queryData.readOneById.additionalRemarks2,
    additionalRemarks3: props.queryData.readOneById.additionalRemarks3,
    assetItemCurrentQuantity: props.queryData.readOneById.assetItemCurrentQuantity,

    assetItemOriginalQuantity: props.queryData.readOneById.assetItemOriginalQuantity,
    barcode: props.queryData.readOneById.barCode,
    businessUnit: props.queryData.readOneById.businessUnit,
    categoryCode6: props.queryData.readOneById.categoryCode6,
    companyNumber: props.queryData.readOneById.companyNumber,
    dateAcquired: props.queryData.readOneById.dateAcquired,
    department: props.queryData.readOneById.department,
    equipmentStatus: props.queryData.readOneById.equipmentStatus,
    itemFunction: props.queryData.readOneById.itemFunction,
    locationDescription: props.queryData.readOneById.locationDescription,
    location: props.queryData.readOneById.location,
    majorAccountingClass: props.queryData.readOneById.majorAccountingClass,

    majorEquipmentClass: props.queryData.readOneById.majorEquipmentClass,
    manufacturer: props.queryData.readOneById.manufacturer,
    newOrUsed: props.queryData.readOneById.newOrUsed,
    originalPurchaser: props.queryData.readOneById.originalPurchaser,
    parentNumber: props.queryData.readOneById.parentNumber,
    physicalOwner: props.queryData.readOneById.physicalOwner,
    poNumber: props.queryData.readOneById.poNumber,
    serialNumber: props.queryData.readOneById.serialNumber,
    updateDate: props.queryData.readOneById.updateDate,
    updateType: props.queryData.readOneById.updateType,
  };
  let requestItemNumber;
  const { data } = useQuery(findRequestNumberFromBarcode, {
    variables: { barcode: initialFormikValues.barcode },
  });
  if (data) {
    requestItemNumber = data.requestNumberFromBarcode.requestItemFYNumber.split('-')[1];
    requestItemNumber = Number(requestItemNumber);
  }

  const createAuditForAsset = (formValues) => {
    let commentStr = comment !== '' ? `${comment} :: Item ${formValues.barcode}` : `Item with barcode ${formValues.barcode} status has changed`;

    const audit = {
      barcode: formValues.barcode,
      createdBy: user.adId,
      lastUpdatedBy: user.adId,
      comment: commentStr,
      updatedOn: new Date(),
      entryType: formValues.updateStatus,
      requestId: requestItemNumber,
    };
    createAudit({ variables: audit }).catch((err) => console.log('Error: /frontend/src/components/RequestForms/EditEOneItemForm//createAuditForAsset', err));
  };

  const formik = useFormik({
    initialValues: initialFormikValues,
    onSubmit: async (values: any, e: any) => {
      if (values.accountNumber) {
        if (!/^[0-9]{4}.[0-9]{4}(\.[0-9]{4})?$/i.test(values.accountNumber)) {
          alert('Invalid account number!');
          return;
        }
      }
      updateEOne({ variables: values })
        .then(() => {
          window.location.reload();
        })
        .catch((err) => {
          console.log('Error: /frontend/src/components/RequestForms/EditEOneItemForm//formik', err);
        });
      createAuditForAsset(values);
      //refresh source grid
      // localStorage.setItem('refresh', 'true');
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className='row justify-content-end'>
        <div className='col-sm-9'>
          <Button type='submit' variant='info' className='mt-3 float-left'>
            Save Changes
          </Button>
          <Button className='mt-3 ml-3 float-left' onClick={() => window.history.back()}>
            Back
          </Button>
        </div>
        <div className='col-sm-3'>
          <Statuses
            formik={formik}
            idName={'updateStatus'}
            value={formik.values.updateStatus}
            selClass={'form-control col-sm'}
            label={'Update Status'}
          />
        </div>
      </div>

      <div className='requestDivider'>Asset Information</div>
      <div className='row'>
        <div className='col-sm-8'>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Asset Number</label>
                <input
                  id='assetNumber'
                  name='assetNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.assetNumber}
                  disabled
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Barcode (Unit Number)</label>
                <input
                  id='barcode'
                  name='barcode'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.barcode}
                  disabled
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Serial Number</label>
                <input
                  id='serialNumber'
                  name='serialNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.serialNumber}
                  required
                  disabled
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Parent Number</label>
                <input
                  id='parentNumber'
                  name='parentNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.parentNumber}
                />
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Company Number</label>
                <select
                  id='companyNumber'
                  name='companyNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.companyNumber}
                  required>
                  {selectDropdowns.companyNumbers.map((value, index) => {
                    return (
                      <option value={value.Co}>
                        {value.Co} - {value.Name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Responsible BU</label>
                <input
                  id='businessUnit'
                  name='businessUnit'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.businessUnit}
                  required
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Account Number</label>
                <input
                  id='accountNumber'
                  name='accountNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.accountNumber}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>PO Number</label>
                <input id='poNumber' name='poNumber' className='form-control col-sm' onChange={formik.handleChange} value={formik.values.poNumber} />
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Description 1</label>
                <input
                  id='description1'
                  name='description1'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description1}
                  required
                  maxLength={30}
                  disabled
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Description 2</label>
                <input
                  id='description2'
                  name='description2'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description2}
                  maxLength={30}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Description 3</label>
                <input
                  id='description3'
                  name='description3'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description3}
                  maxLength={30}
                />
              </div>
            </div>
          </div>

          <div className='requestDivider'>Location</div>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Location</label>
                <input id='location' name='location' className='form-control col-sm' onChange={formik.handleChange} value={formik.values.location} />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Original Quantity</label>
                <input
                  id='assetItemOriginalQuantity'
                  name='assetItemOriginalQuantity'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.assetItemOriginalQuantity}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Current Quantity</label>
                <input
                  id='assetItemCurrentQuantity'
                  name='assetItemCurrentQuantity'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.assetItemCurrentQuantity}
                />
              </div>
            </div>
          </div>

          <div className='requestDivider'>Category Codes 1-10</div>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Major Accounting Class</label>
                <select
                  id='majorAccountingClass'
                  name='majorAccountingClass'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.majorAccountingClass}>
                  {selectDropdowns.majorAccountingClasses.map((value, index) => {
                    return (
                      <option value={value.code}>
                        {value.code} - {value.description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Major Equipment Class</label>
                <select
                  className='form-control col-sm'
                  id='majorEquipmentClass'
                  name='majorEquipmentClass'
                  onChange={formik.handleChange}
                  value={formik.values.majorEquipmentClass}>
                  {selectDropdowns.majorEquipmentClasses.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Manufacturer</label>
                <select
                  id='manufacturer'
                  name='manufacturer'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.manufacturer}>
                  {selectDropdowns.manufacturers.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Department</label>
                <select
                  id='department'
                  name='department'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.department}>
                  {selectDropdowns.departments.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Function</label>
                <select
                  id='itemFunction'
                  name='itemFunction'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.itemFunction}>
                  {selectDropdowns.functions.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Category Code 6</label>
                <select
                  id='categoryCode6'
                  name='categoryCode6'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.categoryCode6}>
                  {selectDropdowns.catCode6.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className='col-sm-4 mt-1'>
          <div className='form-group'>
            <label>Add Comment</label>
            <textarea className='form-control col-sm' id='comment' rows={1} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <Comments barcode={props.queryData.readOneById.barCode} style={{ height: '450px' }} />
        </div>
      </div>

      <div className='requestDivider'>Additional Info</div>
      <div className='row justify-content-start'>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks</label>
            <input
              id='additionalRemarks'
              name='additionalRemarks'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.additionalRemarks}
              maxLength={30}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks 2</label>
            <input
              id='additionalRemarks2'
              name='additionalRemarks2'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.additionalRemarks2}
              maxLength={30}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks 3</label>
            <input
              id='additionalRemarks3'
              name='additionalRemarks3'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.additionalRemarks3}
              maxLength={25}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Physical Owner</label>
            <select
              id='physicalOwner'
              name='physicalOwner'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.physicalOwner}>
              {selectDropdowns.physicalOwners.map((value, index) => {
                return (
                  <option value={value.Code}>
                    {value.Code} - {value.Description}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
      <div className='row justify-content-start'>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Original Purchaser</label>
            <select
              id='originalPurchaser'
              name='originalPurchaser'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.originalPurchaser}>
              {selectDropdowns.originalPurchasers.map((value, index) => {
                return (
                  <option value={value.Code}>
                    {value.Code} - {value.Description}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>New or Used {<i className='fa fa-clone' />} </label>
            <select id='newOrUsed' name='newOrUsed' className='form-control' onChange={formik.handleChange} value={formik.values.newOrUsed}>
              <option value='N'>New</option>
              <option value='U'>Used</option>
            </select>
          </div>
        </div>
        {/* <div className='col-sm-2'>
          <div className='form-group'>
            <label>New or Used</label>
            <input id='newOrUsed' name='newOrUsed' className='form-control col-sm' onChange={formik.handleChange} value={formik.values.newOrUsed} />
          </div>
        </div> */}
      </div>
    </Form>
  );
};

export default EditEOneItemForm;
